import React, { useEffect, useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BusySpinner from '../../common/BusySpinner';
import ScreenBusySpinner from '../../common/ScreenBusySpinner';
import { AppDispatch, RootState } from '../../../data/store';
import { fetchProject } from '../../../data/thunks/projectThunk';
import {
	fetchProject as fetchProjectService,
	updateProject,
} from '../../../services/project-service';
import {
	ProjectCategories,
	ProjectStatuses,
} from '../../../data/models/project';
import MuiChip from '@mui/material/Chip';
import dayjs from 'dayjs';
import { styled } from 'styled-components';
import Menu from '../../common/Menu';
import ProjectNameChangeModal from './ProjectNameChangeModal';
import ProjectSubStatusChangeModal from './ProjectSubStatusChangeModal';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ProjectArchiveModal from './ProjectArchiveModal';
import ProjectUnarchiveModal from './ProjectUnarchiveModal';
import BusinessIcon from '@mui/icons-material/Business';
import ProjectChangeClient from './ProjectChangeClientModal';
import { SubHeader } from '../../common/layout/PageHeader';
import { Divider } from '@mui/material';
import GreenCheckIcon from '../../common/GreenCheckIcon';
import RedExclamationIcon from '../../common/RedExclamationIcon';
import ProjectStatusDisplay from '../ProjectStatusDisplay';
import Paper, { PaperHeader, PaperHeaderNav } from '../../common/layout/Paper';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import ProjectTypeDisplay from '../ProjectTypeDisplay';
import ProjectChangeTypeModal from './ProjectChangeTypeModal';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CachedIcon from '@mui/icons-material/Cached';
import { FlexCenterRow } from '../../common/layout/Content';
import Tabs from '../../common/Tabs';
import { getIconFromGoogleMimeType } from '../../../utils/company-utils';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GoogleDriveIcon from '../../../images/google-drive.png';
import GoogleChatIcon from '../../../images/google-chat.png';

import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ProjectAddTemplateModal from './ProjectAddTemplateModal';
import { refreshProjectSuccess } from '../../../data/stores/projectStore';
import FullscreenModal from '../../common/FullscreenModal';
import Select from '../../common/form/Select';

import ProjectInformation from './ProjectInformation/ProjectInformation';
import ProjectTemplates from './ProjectTemplates';

export default function ProjectDetailsScreen() {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const match = useMatch('projects/:projectId/*');
	const projectId = match?.params?.projectId;

	const [projectNameChangeModalOpen, setProjectNameChangeModalOpen] =
		useState(false);
	const [
		projectSubStatusChangeModalOpen,
		setProjectSubStatusChangeModalOpen,
	] = useState(false);
	const [archiveProjectModal, setArchiveProjectModal] = useState(false);
	const [unarchiveProjectModal, setUnarchiveProjectModal] = useState(false);
	const [changeClientModal, setChangeClientModal] = useState(false);
	const [changeTypeModal, setChangeTypeModal] = useState(false);
	const [addTemplateModal, setAddTemplateModal] = useState(false);
	const [currentTab, setCurrentTab] = useState('project-info');
	const [projectInfoFullscreen, setProjectInfoFullscreen] = useState(false);

	const { project } = useSelector((state: RootState) => state.projectState);

	useEffect(() => {
		if (projectId && projectId !== project.id) {
			dispatch(fetchProject(projectId));
		}
	}, [projectId, dispatch, project.id]);

	useEffect(() => {
		const checkStatuses = () => {
			if (
				project.status === ProjectStatuses.INITIALIZING.id ||
				project.status === ProjectStatuses.UPDATING.id
			) {
				return true;
			}

			const fileStatuses = project.projectInitializationFileStatuses;
			if (
				!fileStatuses ||
				project.projectInitializationStatus?.googleChatSpaceStatus ===
					'INITIALIZING'
			) {
				return true;
			}
			return Object.values(fileStatuses).some(
				(fileStatus: any) =>
					fileStatus.status === 'CREATED' ||
					fileStatus.status === 'INITIALIZING'
			);
		};

		if (checkStatuses() && projectId) {
			const interval = setInterval(async () => {
				const updatedProject = await fetchProjectService(projectId);
				dispatch(refreshProjectSuccess(updatedProject));
			}, 2000);

			return () => {
				clearInterval(interval);
			};
		}
	}, [
		project.status,
		projectId,
		dispatch,
		project.projectInitializationFileStatuses,
		project.projectInitializationStatus?.googleChatSpaceStatus,
	]);

	const activeProjectUpdate = useMemo(() => {
		const sortedUpdates = [...project.projectUpdates].sort(
			(a, b) =>
				new Date(a.created).getTime() - new Date(b.created).getTime()
		);
		return sortedUpdates.find(
			(update: any) => update.status === 'UPDATING'
		);
	}, [project.projectUpdates]);

	const menuItems = useMemo(() => {
		if (project.status === ProjectStatuses.ACTIVE.id) {
			const menuItems: any[] = [];
			menuItems.push({
				display: 'Update Project Name',
				onClick: () => setProjectNameChangeModalOpen(true),
				// disabled:
				// 	!project.projectInitializationStatus.googleFilesUpdated &&
				// 	dayjs()
				// 		.subtract(24, 'hour') // is less than 24 hours
				// 		.isBefore(dayjs(project.created)),
				icon: (
					<DriveFileRenameOutlineOutlinedIcon
						color={'primary'}
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			});
			menuItems.push({
				display: 'Update Sub Status',
				onClick: () => setProjectSubStatusChangeModalOpen(true),
				icon: (
					<DriveFileRenameOutlineOutlinedIcon
						color={'primary'}
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			});
			menuItems.push({
				display: 'Archive Project',
				onClick: () => setArchiveProjectModal(true),
				icon: (
					<ArchiveOutlinedIcon
						color={'primary'}
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			});
			menuItems.push({
				display: 'Change Client',
				onClick: () => setChangeClientModal(true),
				icon: (
					<BusinessIcon
						color={'primary'}
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			});
			menuItems.push({
				display: 'Change Project Type',
				onClick: () => setChangeTypeModal(true),
				icon: (
					<CategoryOutlinedIcon
						color={'primary'}
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			});
			menuItems.push({
				display: 'View Inactive Templates',
				onClick: () => setAddTemplateModal(true),
				icon: (
					<VisibilityOffOutlinedIcon
						color={'primary'}
						fontSize={'medium'}
						sx={{ marginRight: 1 }}
					/>
				),
			});
			return menuItems;
		} else if (project.status === ProjectStatuses.ARCHIVED.id) {
			return [
				{
					display: 'Unarchive Project',
					onClick: () => setUnarchiveProjectModal(true),
					icon: (
						<ArchiveOutlinedIcon
							color={'primary'}
							fontSize={'medium'}
							sx={{ marginRight: 1 }}
						/>
					),
				},
			];
		} else {
			return [];
		}
	}, [
		project.status,
		project.projectInitializationStatus.googleFilesUpdated,
		project.created,
	]);

	const panels = [
		// {
		// 	label: 'Overview',
		// 	key: 'overview',
		// 	component: <ProjectInformation />,
		// 	onclick: () => setCurrentTab('overview'),
		// },
		{
			label: 'Project Information',
			key: 'project-info',
			component: (
				<ProjectInformation
					onToggleFullscreen={() =>
						setProjectInfoFullscreen(!projectInfoFullscreen)
					}
				/>
			),
			onclick: () => setCurrentTab('project-info'),
		},
		{
			label: 'Templates',
			key: 'templates',
			component: <ProjectTemplates />,
			onclick: () => setCurrentTab('templates'),
		},
		{
			label: (
				<TabLabel>
					Chat Space{' '}
					{project.chatSpaceId ? (
						<TabIcon src={GoogleChatIcon} alt={'Google Chat'} />
					) : (
						<StyledScheduleIcon />
					)}
				</TabLabel>
			),
			key: 'chat-space',
			component: <div>hi</div>,
			onclick: () =>
				window.open(
					`https://mail.google.com/chat/u/2/#chat/space/${project.chatSpaceId}`,
					'_blank'
				),
		},
		{
			label: (
				<TabLabel>
					Drive{' '}
					{project.driveId && project.folderId ? (
						<TabIcon src={GoogleDriveIcon} alt={'Google Drive'} />
					) : (
						<StyledScheduleIcon />
					)}
				</TabLabel>
			),
			key: 'drive',
			component: <div>hi</div>,
			onclick: () =>
				window.open(
					`https://drive.google.com/drive/u/${project.driveId}/folders/${project.folderId}`,
					'_blank'
				),
		},
	];

	if (projectId !== project.id) {
		return <BusySpinner />;
	}

	return (
		<>
			<ScreenBusySpinner />
			<ProjectNameChangeModal
				project={project}
				open={projectNameChangeModalOpen}
				handleClose={() => setProjectNameChangeModalOpen(false)}
			/>
			<ProjectSubStatusChangeModal
				project={project}
				open={projectSubStatusChangeModalOpen}
				handleClose={() => setProjectSubStatusChangeModalOpen(false)}
			/>
			<ProjectArchiveModal
				project={project}
				open={archiveProjectModal}
				handleClose={() => setArchiveProjectModal(false)}
			/>
			<ProjectUnarchiveModal
				project={project}
				open={unarchiveProjectModal}
				handleClose={() => setUnarchiveProjectModal(false)}
			/>
			<ProjectChangeClient
				project={project}
				open={changeClientModal}
				handleClose={() => setChangeClientModal(false)}
			/>
			<ProjectChangeTypeModal
				project={project}
				open={changeTypeModal}
				handleClose={() => setChangeTypeModal(false)}
			/>
			<ProjectAddTemplateModal
				open={addTemplateModal}
				handleClose={() => setAddTemplateModal(false)}
			/>
			<ScreenContainer>
				<HeaderPaper gradientBorder>
					<HeaderContainer>
						<NamespaceContainer>
							<PaperHeader>
								<PaperHeaderNav
									onClick={() => navigate('/projects')}
								>
									Projects <ChevronRightOutlinedIcon />
								</PaperHeaderNav>
								<Namespace>{project.namespace}</Namespace>
							</PaperHeader>
							<Menu
								menuItems={menuItems}
								variant={'outlined'}
								color={'primary'}
								alternativeIcon={() => <SettingsOutlinedIcon />}
							/>
						</NamespaceContainer>
						<SubHeader>
							<ProjectStatusDisplay
								status={project.status}
								subStatus={project.subStatus}
							/>
							{/* <span style={{ marginLeft: 10 }}></span> */}
							<ProjectTypeDisplay type={project.type} />
						</SubHeader>
						{activeProjectUpdate && (
							<UpdatingMessage>
								{activeProjectUpdate.message}
							</UpdatingMessage>
						)}
						<ClientName>{project.client.name}</ClientName>
						{project.archivedBy && (
							<SubHeader>
								Archived:{' '}
								{dayjs(project.archivedBy.date).format(
									'MMM D, YYYY | h:mm A'
								)}{' '}
								| {project.archivedBy.name}
							</SubHeader>
						)}
						<SubHeader>
							Created:{' '}
							{dayjs(project.created).format(
								'MMM D, YYYY | h:mm A'
							)}
						</SubHeader>
						<BuildingStatus project={project} />
					</HeaderContainer>
				</HeaderPaper>
				<TabsContainer>
					<Tabs
						panels={panels}
						currentPath={currentTab}
						variant={'pill'}
					/>
				</TabsContainer>
			</ScreenContainer>

			{/* Fullscreen Project Information Modal */}
			<FullscreenModal
				open={projectInfoFullscreen}
				onClose={() => setProjectInfoFullscreen(false)}
			>
				<ProjectInformation
					onToggleFullscreen={() => setProjectInfoFullscreen(false)}
					isFullscreen={true}
				/>
			</FullscreenModal>
		</>
	);
}

const TabsContainer = styled.div`
	margin-top: 5px;
	flex: 1;
	min-height: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;

const TabLabel = styled.div`
	display: flex;
	align-items: center;
`;

const TabIcon = styled.img`
	height: 20px;
	margin-left: 7px;
	object-fit: cover;
	cursor: pointer;
`;

const BuildingStatus = (props) => {
	const { project } = props;

	if (!project.chatSpaceId) {
		return (
			<BuildingStatusText className="fade-in">
				Building Chat Space...
			</BuildingStatusText>
		);
	}
	if (
		project.projectInitializationStatus.googleDriveStatus === 'INITIALIZING'
	) {
		return (
			<BuildingStatusText className="fade-in">
				Building Project Folders...
			</BuildingStatusText>
		);
	}
	if (
		project.projectInitializationFileStatuses[
			ProjectCategories.LEGAL_AND_CONTRACT.id
		]?.status === 'INITIALIZING'
	) {
		return (
			<BuildingStatusText className="fade-in">
				Building Legal Templates...
			</BuildingStatusText>
		);
	}
	if (
		project.projectInitializationFileStatuses[ProjectCategories.CREATIVE.id]
			?.status === 'INITIALIZING'
	) {
		return (
			<BuildingStatusText className="fade-in">
				Building Creative Templates...
			</BuildingStatusText>
		);
	}
	if (
		project.projectInitializationFileStatuses[
			ProjectCategories.PRODUCTION.id
		]?.status === 'INITIALIZING'
	) {
		return (
			<BuildingStatusText className="fade-in">
				Building Production Templates...
			</BuildingStatusText>
		);
	}
	if (
		project.projectInitializationFileStatuses[
			ProjectCategories.POST_PRODUCTION.id
		]?.status === 'INITIALIZING'
	) {
		return (
			<BuildingStatusText className="fade-in">
				Building Post-Production Templates...
			</BuildingStatusText>
		);
	}
	return null;
};

const BuildingStatusText = styled.div`
	font-weight: 500;
	font-size: 16px;
	margin-top: 10px;
	opacity: 0;
	animation: fadeIn 0.2s ease-in forwards;

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
`;

const DriveStatus = (props) => {
	const { status } = props;

	return (
		<ChipContainer>
			{status === 'INITIALIZING' ? (
				<QueuedChip
					className="fade-in"
					label={
						<FlexCenterRow>
							<StyledScheduleIcon /> Queued
						</FlexCenterRow>
					}
				/>
			) : status === 'SUCCESS' || status === 'CREATED' ? (
				<div className="fade-in">
					<GreenCheckIcon />
				</div>
			) : (
				<BuildingChip
					className="fade-in"
					label={
						<FlexCenterRow>
							<StyledCachedIcon className="spin" /> Building
						</FlexCenterRow>
					}
				/>
			)}
		</ChipContainer>
	);
};

const StyledScheduleIcon = styled(ScheduleIcon)`
	color: ${({ theme }) => theme.palette.action.pending};
	font-size: 20px !important;
	margin-left: 5px;
`;

const ChipContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: 15px;
	margin-right: 15px;
	font-style: italic;

	.fade-in {
		opacity: 0;
		animation: fadeIn 0.2s ease-in forwards;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
`;

const StyledCachedIcon = styled(CachedIcon)`
	margin-right: 5px;
	color: ${({ theme }) => theme.palette.primary.main};

	&.spin {
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
`;

const QueuedChip = styled(MuiChip)`
	background-color: ${({ theme }) =>
		theme.palette.status.queued.background} !important;
	& .MuiChip-label {
		color: ${({ theme }) => theme.palette.status.queued.text} !important;
	}
	height: 24px !important;
	width: 100px;
`;

const BuildingChip = styled(MuiChip)`
	background-color: ${({ theme }) =>
		theme.palette.status.building.background} !important;
	& .MuiChip-label {
		color: ${({ theme }) => theme.palette.status.building.text} !important;
	}
	height: 35px !important;
	width: 100px;
	border-radius: 20px !important;
`;

const ScreenContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: calc(100vh - 98px);
	position: relative;
	flex: 1;
	padding: 1px;
	contain: content;
`;

const Namespace = styled.div`
	display: inline;
	margin-right: 20px;
	color: ${({ theme }) => theme.palette.text.primary};
`;

const HeaderContainer = styled.div`
	padding: 10px;
	display: flex;
	flex-direction: column;
`;

const HeaderPaper = styled(Paper)`
	display: flex;
	flex-direction: column;
	margin-bottom: 5px;
	flex-shrink: 0;
`;

const NamespaceContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ClientName = styled.div`
	font-size: 18px;
	margin-top: 5px;
	font-weight: 500;
`;

const UpdatingMessage = styled.div`
	font-size: 0.8rem;
	color: ${({ theme }) => theme.palette.text.label};
	margin-bottom: 5px;
	margin-top: 5px;
`;
